import React, {useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import {Box, Divider as MuiDivider, Grid, ThemeProvider, Typography} from "@material-ui/core";
import CustomBreadcrumbs from "../../../../../components/CustomBreadcrumbs";
import {useParams} from "react-router-dom";
import {AppDispatchType, useSelector} from "../../../../../redux/store";
import {fetchProperty as propertyFetchProperty} from "../../../../../services/propertyService";
import * as types from "../../../../../constants";
import {useDispatch} from "react-redux";
import {AppStateType} from "../../../../../redux/reducers";
import styled from "styled-components/macro";
import {spacing} from "@material-ui/system";
import InfoBlock from "../InfoBlock";
import {
  clearProperty,
  clearPropertyAccessibilityInfo,
  clearPropertyFreeTextInfos
} from "../../../../../redux/actions-declarations/property";
import CustomTabs from "../../../../../components/CustomTabs";
import ImagesTab from "./components/tabs/images-tab/ImagesTab";
import PracticalInformationTab from "./components/tabs/practical-information-tab/PracticalInformationTab";
import LanguageSettingTab from "./components/tabs/language-setting-tab/LanguageSettingTab";
import {fetchUnitImagesByProperty} from "../../../../../redux/actions/unitImagesActions";
import {clearUnitImagesByCompanyId} from "../../../../../redux/actions-declarations/unitImage";
import {Skeleton as MuiSkeleton} from "@material-ui/lab";
import {clearUnits, fetchUnits} from "../../../../../redux/actions/unitsActions";
import {clearA11yesByProperty, fetchA11yesByProperty} from "../../../../../redux/actions/a11yesActions";
import {GradientButton} from "../../../property-page-components/PropertyForm";
import {Launch, MoreVert} from "@material-ui/icons";
import DropdownButton from "./components/DropdownButton";
import {Fade} from "@mui/material";
import HistoryDialog from "./components/history-dialog/HistoryDialog";
import createTheme from "../../../../../theme";
import {THEMES} from "../../../../../constants";
import PublishedWidget from "../PublishedWidget";
import {IUser} from "../../../../../models/User";
import {getUserProfile} from "../../../../../selectors";
import {
  clearCompanyByProperty,
  fetchCompany,
  fetchCompanyByProperty
} from "../../../../../redux/actions/companyActions";
import {useTranslation} from "react-i18next";
import {fetchA11yStandard} from "../../../../../redux/actions/a11yStandardActions";
import ButtonSettingsTab from "./components/tabs/button-settings-tab/ButtonSettingsTab";
import {fetchButtonSettings} from "../../../../../services/widgetButtonSettingsService";
import {getTextByCurrentLanguage} from "../../../../../utils/functions";
import WebmasterInstructionTab from "./components/tabs/webmaster-instruction-tab/WebmasterInstructionTab";

const Skeleton = styled(MuiSkeleton)`
  background-color: #232F3E;
  max-width: 300px;
  @media (max-width: 768px) {
    max-width: none;
  }
`

const CustomGradientButton = styled(GradientButton)`
  height: 42px;
  @media (max-width: 599px) {
    width: 100%;
  }
`

const CustomBox = styled(Box)`
  display: flex;
  justify-content: flex-end;
  @media (max-width: 600px) {
    flex-direction: column;
    & > :nth-child(1) {
      margin-bottom: 10px
    }
  }
`

const Divider = styled(MuiDivider)(spacing);

const EditWidget = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const params: {id: string} = useParams()
  const user = useSelector((state) => state.authReducer.user);
  const property = useSelector((state: AppStateType) => state.propertyReducer.property)!
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false)
  const [buttonSettings, setButtonSettings] = useState<any>(null)
  useEffect(() => {
    dispatch(clearUnits())
    dispatch(async (dispatch: AppDispatchType) => {
      return propertyFetchProperty(params.id, user!.token)
        .then((response: any) => {
          dispatch({
            type: types.FETCH_PROPERTY,
            property: response.data,
          });
        })
    });
    dispatch(fetchUnitImagesByProperty(user!.token, params.id))
    dispatch(fetchUnits(params.id, user!.token))
    dispatch(fetchA11yesByProperty(user!.token, params.id))
    dispatch(fetchA11yStandard(user!.token))
    return () => {
      dispatch(clearUnits())
      dispatch(clearProperty())
      dispatch(clearCompanyByProperty())
      dispatch(clearUnitImagesByCompanyId())
      dispatch(clearA11yesByProperty())
      dispatch(clearPropertyAccessibilityInfo())
      dispatch(clearPropertyFreeTextInfos())
    }
  }, [])

  useEffect(() => {
    if (property && property.company) {
      dispatch(fetchCompanyByProperty(property.company, user!.token))
    }
  }, [property]);

  const getTabContent = (tab: number) => {
    const TABS: {[key: number]: React.ReactNode} = {
      0: <ImagesTab/>,
      1: <PracticalInformationTab/>,
      2: <LanguageSettingTab/>,
      3: <ButtonSettingsTab/>,
      4: <WebmasterInstructionTab/>
    }
    return TABS[tab]
  }

  return (
    <Box p={12} className={`disable-scrolls`} overflow="scroll">
      <Helmet title={t('edit_widget')} />
      <Typography variant="h3" gutterBottom display="inline">
        {t('widget')}
      </Typography>
      <CustomBreadcrumbs
        current={getTextByCurrentLanguage(property?.name)}
        before={[
          {to: '/', title: t('dashboard')},
          {to: '/widget', title: t('widget')}
        ]}
      />
      <Divider my={6} />
      <InfoBlock />
      <Box pt={4}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={5} md={6}>
            {getTextByCurrentLanguage(property?.name) ? (
              <Fade in={true}>
                <Typography variant="h3" style={{fontWeight: 400}}>
                  {getTextByCurrentLanguage(property.name)}
                </Typography>
              </Fade>
            ) : <Skeleton variant='text' height={40}/>}
          </Grid>
          <Grid item xs={12} sm={7} md={6}>
            <CustomBox>
              <PublishedWidget
                propertyId={property?.id}
                published={property?.published}
              />
              <Box display='flex' flexDirection='row'>
                <CustomGradientButton
                  disabled={!property?.published}
                  onClick={() => window.open(`https://accessibility-widget.handiscover.com/page/property/${params.id}`, '_blank')}
                >
                  {t('view_live_widget')}
                  <Box ml={1}>
                    <Launch />
                  </Box>
                </CustomGradientButton>
                <Box ml={2}>
                  <DropdownButton items={[t('history')]} onClick={() => setOpenHistoryDialog(true)}>
                    <MoreVert />
                  </DropdownButton>
                </Box>
              </Box>
            </CustomBox>
          </Grid>
        </Grid>
      </Box>
      <Box mt={4}>
         <CustomTabs
           tabs={[
             t('image_list'),
             t('practical_information'),
             t('language_setting'),
             t('button_settings'),
             t('webmaster_instruction')
           ]}
           value={selectedTab}
           setValue={setSelectedTab}
         />
      </Box>
      <Box mt={5}>
        {getTabContent(selectedTab)}
      </Box>
      <ThemeProvider theme={createTheme(THEMES.LIGHT)}>
        <HistoryDialog
          open={openHistoryDialog}
          title={getTextByCurrentLanguage(property?.name)}
          onClose={() => setOpenHistoryDialog(false)}
        />
      </ThemeProvider>
    </Box>
  )
}

export default EditWidget